/**
 * `Date.toISOString()` (e.g. "2026-07-21T11:07:20.113Z") isn't a MySQL
 * DATETIME literal — the 'T', milliseconds, and 'Z' all make it reject
 * with SQLSTATE[22007]. Every datetime the API returns is a naive local
 * "YYYY-MM-DD HH:MM:SS" string (see formatDateTime's `.replace(' ', 'T')`
 * below) — this produces the same shape for "now" going the other way.
 */
export function toApiDateTime(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function formatDateTime(value: string | null): string {
  if (!value) {
    return '—';
  }
  return new Date(value.replace(' ', 'T')).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export function formatRelativeTime(value: string | null): string {
  if (!value) {
    return 'Never';
  }
  const date = new Date(value.replace(' ', 'T'));
  const diffMs = date.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / 60_000);

  const divisions: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 60 * 24 * 365],
    ['month', 60 * 24 * 30],
    ['day', 60 * 24],
    ['hour', 60],
    ['minute', 1],
  ];

  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

  for (const [unit, minutesInUnit] of divisions) {
    if (Math.abs(diffMinutes) >= minutesInUnit || unit === 'minute') {
      return formatter.format(Math.round(diffMinutes / minutesInUnit), unit);
    }
  }
  return formatter.format(0, 'minute');
}

/** Auto-compact number formatting for stat-tile values: 1,284 / 12.9K / 4.2M — per the dataviz skill's figure spec. */
export function formatCompactNumber(value: number): string {
  return new Intl.NumberFormat(undefined, { notation: value >= 1000 ? 'compact' : 'standard', maximumFractionDigits: 1 }).format(value);
}

/**
 * The org's configured default currency (Organization Settings >
 * Localization > Currencies > "Set default", which also writes
 * organizations.currency server-side — see SettingsModel::setDefaultCurrency).
 * Applied once on login (AuthContext) and again whenever the default
 * changes, so every formatCurrency() call that doesn't pass its own
 * record-specific currency (an invoice/deal's own currency column, for
 * example — those should keep taking precedence) picks up the org's
 * choice instead of a hardcoded 'USD'. Default parameters in JS
 * re-evaluate on every call, so this stays live without touching any
 * of formatCurrency's ~24 call sites.
 */
let orgDefaultCurrency = 'USD';

export function setOrgDefaultCurrency(currencyCode: string | null | undefined): void {
  if (currencyCode) {
    orgDefaultCurrency = currencyCode;
  }
}

export function formatCurrency(value: number, currency = orgDefaultCurrency): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    notation: value >= 100_000 ? 'compact' : 'standard',
    maximumFractionDigits: value >= 100_000 ? 1 : 0,
  }).format(value);
}

export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours === 0) {
    return `${mins}m`;
  }
  return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
}

export function formatShortDate(value: string | null): string {
  if (!value) {
    return '—';
  }
  return new Date(value.replace(' ', 'T')).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
